import { useMemo, useState } from "react";
import PlusIcon from "../../../../icons/plus.icon";
import { Spinner } from "react-bootstrap";
import ContentLoader from "react-content-loader";
import { HGHighlightText } from "../../../../App/App";
import { Integration } from "./add-tool.dropdown";
import { useNavigate } from "react-router-dom";
import "./add-tool.dropdown.scss";
import cn from "../../../../../Utils/class-names.utils";
import ToolIcon from "../../../../icons/tool-icon.component";
import SearchInput from "../../../../search-input/search-input.component";

interface IMenu {
  toolsInStackList?: Integration[];
  toolsList?: Integration[];
  searchQuery: string;
  isLoading: boolean;
  isAnswerNoLoading?: boolean;
  showNoAnswerButton?: boolean;
  showOnlyPracticeTools?: boolean;
  showToolsInStack?: boolean;
  onSearchChange: (value: string) => void;
  onAddTool: (integration: Integration) => any;
  onStackToolClick?: (integration: Integration) => void;
  answerNo: () => void;
}

export const AddToolDropdownMenu = ({ 
  toolsInStackList = [], toolsList = [], searchQuery, onSearchChange, isLoading, onAddTool, answerNo, isAnswerNoLoading, showNoAnswerButton = true, showOnlyPracticeTools = false,
  onStackToolClick, showToolsInStack = true
}: IMenu) => {
  const navigate = useNavigate();
  const [toolLoadingId, setToolLoadingId] = useState<number | null>(null);

  const initLoading = isLoading && !toolsList?.length;

  const handleAddTool = async (integration: Integration) => {
    if (toolLoadingId === integration?.IntegrationId) return;
    setToolLoadingId(integration?.IntegrationId);
    await onAddTool(integration)
    setToolLoadingId(null);
  };

  const handleStackToolClick = async (integration: Integration) => {
    if (!onStackToolClick) return;
    if (toolLoadingId === integration?.IntegrationId) return;
    setToolLoadingId(integration?.IntegrationId);
    await onStackToolClick(integration)
    setToolLoadingId(null);
  };

  const handleBuildStack = () => {
    // navigate.push({
    //   pathname: ROUTE_PATHS.mspv2.securityStack,
    //   search: `?variant=v2`,
    //   state: {
    //     tab: TOOLSET_TABS.YOUR_STACK
    //   }
    // });
    // document.body.click();
  };

  const combinedToolsList = useMemo(() => {
    if (!showToolsInStack) return toolsList;
    return [...toolsInStackList, ...toolsList];
  }, [toolsInStackList, toolsList, showToolsInStack]);

  return (
    <div
      className="add-tool-dropdown"
      role="listbox"
      aria-label="Tool selection dropdown"
    >
      <div className="add-tool-dropdown__search-container">
        <SearchInput value={searchQuery} onChange={onSearchChange} placeholder="Search" className="bg-white" />

        <div className="add-tool-dropdown__tools-container hide-scrollbar">
          {initLoading && (
             <div className="d-flex flex-column gap-4">
                {
                  Array(7).fill(0).map((_, index) => (
                    <MenuItemLoader key={index} />
                  ))
                }
             </div>
          )}
          {!initLoading && (
            <div className="d-flex flex-column gap-8">
              {searchQuery ? (
                <>
                  <div>
                    <span className="add-tool-dropdown__title">
                      Results
                    </span>
                    {combinedToolsList
                    ?.filter(item => showOnlyPracticeTools ? item.IsPractice : true)
                    ?.map((item: Integration) => (
                      <ToolItem key={item.IntegrationId} item={item} searchQuery={searchQuery} toolLoadingId={toolLoadingId} handleAddTool={handleAddTool} />
                    ))}
                  </div>
                </>
              ) : (
                <>
                  {showToolsInStack && (
                    <div>
                      <span className="add-tool-dropdown__title">
                        {showOnlyPracticeTools ? 'Practices' : 'Tools'} in your stack
                      </span>
                      {toolsInStackList
                      ?.filter(item => showOnlyPracticeTools ? item.IsPractice : true)
                      ?.map((item: Integration) => (
                        <ToolItem key={item.IntegrationId} item={item} searchQuery={searchQuery} toolLoadingId={toolLoadingId} handleAddTool={handleStackToolClick} />
                      ))}
                      <div className="add-tool-dropdown__no-tool-container">
                        <button
                          className="my-button add-tool-dropdown__no-tool-button"
                          role="option"
                          aria-selected={false}
                          tabIndex={0}
                          onClick={handleBuildStack}
                        >
                          <span className="add-tool-dropdown__no-tool-text w-100">
                            <div className="d-flex align-items-center gap-8">
                              <div className="d-flex align-items-center justify-content-center" style={{ marginBottom: '2px' }}>
                                <PlusIcon fill="#008FD4" />
                              </div>
                              <span>
                                Build your stack
                              </span>
                            </div>
                          </span>
                        </button>
                      </div>
                    </div>
                  )}
                  <div>
                    <span className="add-tool-dropdown__title">
                      {showOnlyPracticeTools ? 'All Practices' : 'All Tools'}
                    </span>
                    {toolsList
                    ?.filter(item => showOnlyPracticeTools ? item.IsPractice : true)
                    ?.map((item: Integration) => (
                      <ToolItem key={item.IntegrationId} item={item} searchQuery={searchQuery} toolLoadingId={toolLoadingId} handleAddTool={handleAddTool} />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>


      <div className="add-tool-dropdown__no-tool-container">
        <button
          className={cn("my-button add-tool-dropdown__no-tool-button", {
            "d-none": !showNoAnswerButton,
          })}
          role="option"
          aria-selected={false}
          tabIndex={0}
          onClick={() => answerNo()}
        >
          <span className="add-tool-dropdown__no-tool-text w-100">
            {searchQuery ? (
              <div className="d-flex align-items-center gap-4">
                <div className="d-flex align-items-center justify-content-center" style={{ marginBottom: '2px' }}>
                  <PlusIcon fill="#008FD4" />
                </div>
                <span>
                  Add "{searchQuery}"
                </span>
              </div>
            ) : (
              <div className="d-flex align-items-center gap-4 justify-content-between w-100">
                <span>
                  I don&apos;t have a {showOnlyPracticeTools ? 'practice' : 'tool'}
                </span>
                {isAnswerNoLoading ? (
                  <div className="d-flex align-items-center" style={{ height: '20px' }}>
                    <Spinner animation="border" variant="secondary" size="sm" style={{
                      width: "12px",
                      height: "12px",
                    }} />
                  </div>
                ) : null}
              </div>
            )}
          </span>
        </button>
      </div>
    </div>
  );
};

interface ToolItemProps {
  item: Integration;
  searchQuery: string;
  toolLoadingId: number | null;
  handleAddTool?: (integration: Integration) => void;
}

const ToolItem = ({ item, searchQuery, toolLoadingId, handleAddTool }: ToolItemProps) => {
  return (
    <button
      className="add-tool-dropdown__tool-button my-button"
      role="option"
      aria-selected={false}
      tabIndex={0}
      onClick={() => handleAddTool?.(item)}
      disabled={!handleAddTool}
    >
      <ToolIcon toolId={item?.IntegrationId} toolIcon={item?.ToolIcon} className="add-tool-dropdown__tool-icon" />

      <span className="add-tool-dropdown__tool-name">
        <span dangerouslySetInnerHTML={{
          __html: HGHighlightText(
            item?.IntegrationName,
            searchQuery
          )
        }} />
      </span>

      <div style={{
        opacity: handleAddTool ? 1 : 0,
      }}>
        {toolLoadingId === item?.IntegrationId ? (
          <div className="d-flex align-items-center" style={{ height: '24px' }}>
            <Spinner animation="border" variant="secondary" size="sm" style={{
              width: "12px",
              height: "12px",
              marginRight: '8px'
            }} />
          </div>
        ) : (
          <div className="my-button styled justify-content-start d-flex align-items-center f-grey add-tool-dropdown__add-button">
            <div className="left-icon">
              <PlusIcon fill="#616778" />
            </div>
            <span className="f-12 line-height-18 f-500">Add</span>
          </div>
        )}
      </div>
    </button>
  )
}

const MenuItemLoader = () => (
  <ContentLoader
    width={'100%'}
    height={'28px'}
  >
    <rect x="0" y="0" rx="4px" ry="4px" width="100%" height = "28px" />
  </ContentLoader>
)
